import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Avatar,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  CreditCard as CreditCardIcon,
  Description as DocumentIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';

const EditEmployee = ({
  isOpen,
  onClose,
  onUpdateEmployee,
  selectedEmployee,
  businessOptions,
  branchOptions,
  departmentOptions,
  designationOptions,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);

  const [formData, setFormData] = useState({
    // Personal Details
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    address: '',
    emergencyContact: '',
    emergencyPhone: '',
    password: '',
    role: '',

    // Company Details
    employeeCode: '',
    businessId: '',
    branchId: '',
    department: '',
    designation: '',
    reportingManager: '',
    dateOfJoining: '',
    employmentType: '',
    workLocation: '',
    shift: '',

    // Bank Details
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountHolderName: '',
    upiId: '',

    // Status
    status: '',
  });

  useEffect(() => {
    if (selectedEmployee) {
      setFormData({
        firstName: selectedEmployee.firstName || selectedEmployee.name?.split(' ')[0] || '',
        lastName: selectedEmployee.lastName || selectedEmployee.name?.split(' ')[1] || '',
        email: selectedEmployee.email || '',
        phone: selectedEmployee.phone || '',
        dateOfBirth: selectedEmployee.dateOfBirth || '',
        gender: selectedEmployee.gender || '',
        address: selectedEmployee.address || '',
        emergencyContact: selectedEmployee.emergencyContact || '',
        emergencyPhone: selectedEmployee.emergencyPhone || '',
        password: selectedEmployee.password || '',
        role: selectedEmployee.role || '',
        employeeCode: selectedEmployee.code || selectedEmployee.employeeCode || '',
        businessId: selectedEmployee.businessId?.toString() || '',
        branchId: selectedEmployee.branchId?.toString() || '',
        department: selectedEmployee.department || '',
        designation: selectedEmployee.designation || '',
        reportingManager: selectedEmployee.reportingManager || '',
        dateOfJoining: selectedEmployee.dateOfJoining || '',
        employmentType: selectedEmployee.employmentType || '',
        workLocation: selectedEmployee.workLocation || '',
        shift: selectedEmployee.shift || '',
        bankName: selectedEmployee.bankName || '',
        accountNumber: selectedEmployee.accountNumber || '',
        ifscCode: selectedEmployee.ifscCode || '',
        accountHolderName: selectedEmployee.accountHolderName || '',
        upiId: selectedEmployee.upiId || '',
        status: selectedEmployee.status || 'active',
      });

      // Set profile photo preview if exists
      if (selectedEmployee.profilePhoto) {
        setProfilePhotoPreview(
          typeof selectedEmployee.profilePhoto === 'string'
            ? selectedEmployee.profilePhoto
            : URL.createObjectURL(selectedEmployee.profilePhoto)
        );
      } else {
        setProfilePhotoPreview(null);
      }
      setProfilePhoto(null);
    }
  }, [selectedEmployee]);

  const handleSubmit = () => {
    const updatedEmployee = {
      ...selectedEmployee,
      firstName: formData.firstName,
      lastName: formData.lastName,
      name: `${formData.firstName} ${formData.lastName}`,
      email: formData.email,
      phone: formData.phone,
      dateOfBirth: formData.dateOfBirth,
      gender: formData.gender,
      address: formData.address,
      emergencyContact: formData.emergencyContact,
      emergencyPhone: formData.emergencyPhone,
      password: formData.password,
      role: formData.role,
      code: formData.employeeCode,
      employeeCode: formData.employeeCode,
      businessId: Number(formData.businessId),
      branchId: Number(formData.branchId),
      department: formData.department,
      designation: formData.designation,
      reportingManager: formData.reportingManager,
      dateOfJoining: formData.dateOfJoining,
      employmentType: formData.employmentType,
      workLocation: formData.workLocation,
      shift: formData.shift,
      bankName: formData.bankName,
      accountNumber: formData.accountNumber,
      ifscCode: formData.ifscCode,
      accountHolderName: formData.accountHolderName,
      upiId: formData.upiId,
      status: formData.status,
      profilePhoto: profilePhoto || selectedEmployee.profilePhoto,
    };

    onUpdateEmployee(updatedEmployee);
    handleClose();
  };

  const handleClose = () => {
    setActiveTab(0);
    setProfilePhoto(null);
    onClose();
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const filteredBranches = formData.businessId
    ? branchOptions.filter((b) => b.businessId.toString() === formData.businessId)
    : branchOptions;

  const getAvatarInitials = (name) => {
    const nameParts = name?.trim().split(' ') || [];
    if (nameParts.length === 1) {
      return name?.charAt(0).toUpperCase() + (name?.charAt(1) || '').toUpperCase();
    }
    const firstName = nameParts[0];
    const lastName = nameParts[nameParts.length - 1];
    return firstName?.charAt(0).toUpperCase() + lastName?.charAt(0).toUpperCase();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: '#536DFE',
          color: 'white',
          py: 2.5,
          px: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          Edit Employee
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
          Update employee information across all sections
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 2 }}
        >
          <Tab label="Personal" icon={<PersonIcon />} iconPosition="start" sx={{ textTransform: 'none' }} />
          <Tab label="Company" icon={<BusinessIcon />} iconPosition="start" sx={{ textTransform: 'none' }} />
          <Tab label="Bank" icon={<CreditCardIcon />} iconPosition="start" sx={{ textTransform: 'none' }} />
          <Tab label="Documents" icon={<DocumentIcon />} iconPosition="start" sx={{ textTransform: 'none' }} />
        </Tabs>

        <Box sx={{ p: 3, maxHeight: '60vh', overflowY: 'auto' }}>
          {/* Personal Tab */}
          {activeTab === 0 && (
            <Box>
              <Card sx={{ boxShadow: 2, borderRadius: 2, mb: 2 }}>
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={1}>
                      <PersonIcon />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Personal Information
                      </Typography>
                    </Box>
                  }
                />
                <CardContent>
                  {/* Profile Photo Upload */}
                  <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                    <Avatar
                      sx={{
                        width: 120,
                        height: 120,
                        mb: 2,
                        border: '4px solid #e0e0e0',
                        bgcolor: '#536DFE',
                        fontSize: '2.5rem',
                        fontWeight: 700,
                      }}
                      src={profilePhotoPreview}
                    >
                      {!profilePhotoPreview && selectedEmployee && getAvatarInitials(selectedEmployee.name)}
                    </Avatar>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="edit-profile-photo-upload"
                      type="file"
                      onChange={handlePhotoChange}
                    />
                    <label htmlFor="edit-profile-photo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                        size="small"
                        sx={{ textTransform: 'none' }}
                      >
                        Change Photo
                      </Button>
                    </label>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      Click to update profile picture
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Password"
                        type="password"
                        size="small"
                        value={formData.password}
                        onChange={(e) => handleChange('password', e.target.value)}
                        placeholder="Leave blank to keep current"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Role *</InputLabel>
                        <Select
                          value={formData.role}
                          onChange={(e) => handleChange('role', e.target.value)}
                          label="Role *"
                        >
                          <MenuItem value="Manager">Manager</MenuItem>
                          <MenuItem value="Trainee">Trainee</MenuItem>
                          <MenuItem value="Employee">Employee</MenuItem>
                          <MenuItem value="HR">HR</MenuItem>
                          <MenuItem value="Finance">Finance</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        size="small"
                        value={formData.firstName}
                        onChange={(e) => handleChange('firstName', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name "
                        size="small"
                        value={formData.lastName}
                        onChange={(e) => handleChange('lastName', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email "
                        type="email"
                        size="small"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number "
                        size="small"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Date of Birth"
                        type="date"
                        size="small"
                        value={formData.dateOfBirth}
                        onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Gender</InputLabel>
                        <Select
                          value={formData.gender}
                          onChange={(e) => handleChange('gender', e.target.value)}
                          label="Gender"
                        >
                          <MenuItem value="male">Male</MenuItem>
                          <MenuItem value="female">Female</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address"
                        size="small"
                        multiline
                        rows={2}
                        value={formData.address}
                        onChange={(e) => handleChange('address', e.target.value)}
                        placeholder="Enter complete address"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Emergency Contact Name"
                        size="small"
                        value={formData.emergencyContact}
                        onChange={(e) => handleChange('emergencyContact', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Emergency Contact Phone"
                        size="small"
                        value={formData.emergencyPhone}
                        onChange={(e) => handleChange('emergencyPhone', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Company Tab */}
          {activeTab === 1 && (
            <Box>
              <Card sx={{ boxShadow: 2, borderRadius: 2, mb: 2 }}>
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={1}>
                      <BusinessIcon />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Company Information
                      </Typography>
                    </Box>
                  }
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Employee Code "
                        size="small"
                        value={formData.employeeCode}
                        onChange={(e) => handleChange('employeeCode', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Date of Joining"
                        type="date"
                        size="small"
                        value={formData.dateOfJoining}
                        onChange={(e) => handleChange('dateOfJoining', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Company </InputLabel>
                        <Select
                          value={formData.businessId}
                          onChange={(e) => {
                            handleChange('businessId', e.target.value);
                            handleChange('branchId', '');
                          }}
                          label="Company *"
                        >
                          {businessOptions.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id.toString()}>
                              {opt.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Branch </InputLabel>
                        <Select
                          value={formData.branchId}
                          onChange={(e) => handleChange('branchId', e.target.value)}
                          label="Branch "
                        >
                          {filteredBranches.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id.toString()}>
                              {opt.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Department </InputLabel>
                        <Select
                          value={formData.department}
                          onChange={(e) => handleChange('department', e.target.value)}
                          label="Department *"
                        >
                          {departmentOptions.map((dept) => (
                            <MenuItem key={dept} value={dept}>
                              {dept}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Designation </InputLabel>
                        <Select
                          value={formData.designation}
                          onChange={(e) => handleChange('designation', e.target.value)}
                          label="Designation "
                        >
                          {designationOptions.map((designation) => (
                            <MenuItem key={designation} value={designation}>
                              {designation}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Employment Type</InputLabel>
                        <Select
                          value={formData.employmentType}
                          onChange={(e) => handleChange('employmentType', e.target.value)}
                          label="Employment Type"
                        >
                          <MenuItem value="Full Time">Full Time</MenuItem>
                          <MenuItem value="Part Time">Part Time</MenuItem>
                          <MenuItem value="Contract">Contract</MenuItem>
                          <MenuItem value="Internship">Internship</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Shift</InputLabel>
                        <Select
                          value={formData.shift}
                          onChange={(e) => handleChange('shift', e.target.value)}
                          label="Shift"
                        >
                          <MenuItem value="morning">Morning</MenuItem>
                          <MenuItem value="evening">Evening</MenuItem>
                          <MenuItem value="night">Night</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Work Location</InputLabel>
                        <Select
                          value={formData.workLocation}
                          onChange={(e) => handleChange('workLocation', e.target.value)}
                          label="Work Location"
                        >
                          <MenuItem value="office">Office</MenuItem>
                          <MenuItem value="remote">Remote</MenuItem>
                          <MenuItem value="hybrid">Hybrid</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Reporting Manager"
                        size="small"
                        value={formData.reportingManager}
                        onChange={(e) => handleChange('reportingManager', e.target.value)}
                        placeholder="Enter reporting manager name"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Bank Tab */}
          {activeTab === 2 && (
            <Box>
              <Card sx={{ boxShadow: 2, borderRadius: 2, mb: 2 }}>
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={1}>
                      <CreditCardIcon />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Bank Information
                      </Typography>
                    </Box>
                  }
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Bank Name"
                        size="small"
                        value={formData.bankName}
                        onChange={(e) => handleChange('bankName', e.target.value)}
                        placeholder="Enter bank name"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Account Holder Name"
                        size="small"
                        value={formData.accountHolderName}
                        onChange={(e) => handleChange('accountHolderName', e.target.value)}
                        placeholder="Enter account holder name"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Account Number"
                        size="small"
                        value={formData.accountNumber}
                        onChange={(e) => handleChange('accountNumber', e.target.value)}
                        placeholder="Enter account number"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="IFSC Code"
                        size="small"
                        value={formData.ifscCode}
                        onChange={(e) => handleChange('ifscCode', e.target.value)}
                        placeholder="Enter IFSC code"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="UPI ID"
                        size="small"
                        value={formData.upiId}
                        onChange={(e) => handleChange('upiId', e.target.value)}
                        placeholder="Enter UPI ID"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Documents Tab */}
          {activeTab === 3 && (
            <Box>
              <Card sx={{ boxShadow: 2, borderRadius: 2, mb: 2 }}>
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={1}>
                      <DocumentIcon />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Document Upload
                      </Typography>
                    </Box>
                  }
                />
                <CardContent>
                  <Grid container spacing={2}>
                    {['Resume/CV', 'Aadhaar Card', 'PAN Card', 'Passport', 'Educational Certificates', 'Experience Letters'].map((doc) => (
                      <Grid item xs={12} sm={6} key={doc}>
                        <Box
                          sx={{
                            border: '2px dashed #e0e0e0',
                            borderRadius: 2,
                            p: 2,
                            textAlign: 'center',
                            '&:hover': { borderColor: '#536DFE', bgcolor: '#f8f9fa' },
                          }}
                        >
                          <UploadIcon sx={{ fontSize: 40, color: '#999', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary" mb={1}>
                            {doc}
                          </Typography>
                          <input
                            type="file"
                            style={{ display: 'none' }}
                            id={`edit-upload-${doc}`}
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          />
                          <label htmlFor={`edit-upload-${doc}`}>
                            <Button
                              variant="outlined"
                              component="span"
                              size="small"
                              sx={{ textTransform: 'none' }}
                            >
                              Choose File
                            </Button>
                          </label>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
        <Button onClick={handleClose} variant="outlined" sx={{ textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
          }}
        >
          Update Employee
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditEmployee;

